import React, { useEffect, useState } from 'react';
import { Box, Card, CardContent, Chip, CircularProgress, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import DescriptionIcon from '@mui/icons-material/Description';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import UploadKyc from '@Components/ShipmentKycUpload/UploadKyc';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootStateType } from '@Store';
import { getShipmentKyc } from '@Reducers/Shipment';

interface Props {
  clearanceDocs: string[];
  loading?: boolean;
  shipmentNumber?: string;
  onRefetchClearanceDocs?: () => void;
}

export function RequiredDocuments({ clearanceDocs, loading, shipmentNumber = '', onRefetchClearanceDocs }: Props) {
  const dispatch = useDispatch<AppDispatch>();
  const { isShipmentKycAdded } = useSelector((state: RootStateType) => state.shipment);

  const [uploadModalOpen, setUploadModalOpen] = useState<boolean>(false);
  const [selectedDocName, setSelectedDocName] = useState<string>('');

  const handleDocClick = (docName: string) => {
    if (!shipmentNumber) return;
    setSelectedDocName(docName);
    setUploadModalOpen(true);
  };

  const handleModalClose = () => {
    setUploadModalOpen(false);
    setSelectedDocName('');
  };

  useEffect(() => {
    if (isShipmentKycAdded && uploadModalOpen) {
      setUploadModalOpen(false);
      setSelectedDocName('');
      if (shipmentNumber) {
        dispatch(getShipmentKyc(shipmentNumber));
      }
      if (onRefetchClearanceDocs) {
        onRefetchClearanceDocs();
      }
    }
  }, [isShipmentKycAdded, uploadModalOpen, shipmentNumber, dispatch, onRefetchClearanceDocs]);

  if (!loading && (!clearanceDocs || clearanceDocs.length === 0)) {
    return null;
  }

  return (
    <>
      <Card
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: '100%',
          borderRadius: '14px',
          border: theme => `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.04), 0 2px 8px -2px rgba(0, 0, 0, 0.03)',
          bgcolor: 'background.paper',
          overflow: 'hidden',
          mb: 3,
        }}
      >
        {/* Card Header */}
        <Box
          sx={{
            p: { xs: 1.5, sm: 2 },
            borderBottom: theme => `1px solid ${alpha(theme.palette.divider, 0.06)}`,
            bgcolor: theme => alpha(theme.palette.primary.main, 0.02),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
            gap: 1.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '10px',
                bgcolor: theme => alpha(theme.palette.primary.main, 0.1),
                color: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <DescriptionIcon sx={{ fontSize: '20px' }} />
            </Box>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontSize: '16px', fontWeight: 500, color: 'primary.main' }}>
                  Required Clearance Documents
                </Typography>
                {!loading && clearanceDocs.length > 0 && (
                  <Chip
                    label={`${clearanceDocs.length} ${clearanceDocs.length === 1 ? 'Document' : 'Documents'}`}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.6875rem',
                      fontWeight: 600,
                      bgcolor: theme => alpha(theme.palette.primary.main, 0.1),
                      color: 'primary.main',
                    }}
                  />
                )}
              </Box>
              <Typography sx={{ fontSize: '14px', fontWeight: 400, color: 'text.secondary' }}>
                Customs clearance documents required for destination compliance
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Card Content */}
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ width: '100%', boxSizing: 'border-box' }}>
            {/* Document List */}
            {loading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2.5 }}>
                <CircularProgress size={20} thickness={4} />
                <Typography sx={{ color: 'text.light', fontSize: '13.5px' }}>
                  Fetching required clearance documents...
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  columnGap: 4,
                  rowGap: 0,
                  width: '100%',
                  boxSizing: 'border-box',
                }}
              >
                {clearanceDocs.map((docName, idx) => (
                  <Box
                    key={`${docName}-${idx}`}
                    onClick={() => handleDocClick(docName)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      py: 1.25,
                      px: 1,
                      borderRadius: '6px',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      minWidth: 0,
                      boxSizing: 'border-box',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: theme => alpha(theme.palette.primary.main, 0.04),
                        '& .upload-icon': {
                          opacity: 1,
                          color: 'primary.main',
                        },
                      },
                    }}
                    title={`Click to upload ${docName}`}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          bgcolor: theme => alpha(theme.palette.primary.main, 0.08),
                          color: 'primary.main',
                          flexShrink: 0,
                          mr: 1.5,
                        }}
                      >
                        <DescriptionIcon sx={{ fontSize: '14px' }} />
                      </Box>
                      <Typography
                        sx={{
                          fontWeight: 500,
                          fontSize: '13.5px',
                          color: theme => theme.palette.text.dark || '#344054',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          minWidth: 0,
                        }}
                      >
                        {docName}
                      </Typography>
                    </Box>
                    <Box
                      className="upload-icon"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        opacity: 0.5,
                        color: 'text.secondary',
                        transition: 'all 0.2s ease',
                        ml: 1,
                        flexShrink: 0,
                      }}
                    >
                      <CloudUploadOutlinedIcon sx={{ fontSize: '18px' }} />
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>

      {uploadModalOpen && (
        <UploadKyc
          open={uploadModalOpen}
          onClose={handleModalClose}
          documentData={{ documentName: selectedDocName } as any}
          shipmentNumber={shipmentNumber}
          allowedFileTypes={[
            'image/png',
            'image/jpg',
            'image/jpeg',
            'application/pdf',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'application/vnd.ms-excel',
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'application/json',
            'text/csv',
            '',
          ]}
        />
      )}
    </>
  );
}

export default RequiredDocuments;
